import React, { useState } from "react";
import { Scale, HelpCircle, ShieldAlert, CheckCircle2, PenLine, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, getErrorMessage } from "@/lib/api";

const field = "w-full bg-ivory border border-champagne rounded-md px-3 py-2 text-sm text-cocoa outline-none focus:border-mauve";

/* "Help me understand the situation" — optional, skippable, ≤2 questions at a time. */
export function ContextCheck({ check, answers, onChange, onSkipAll, onAnswered, busy }) {
  if (!check || !check.questions?.length) return null;
  const set = (id, v) => onChange({ ...answers, [id]: v });
  const skip = (id) => onChange({ ...answers, [id]: "", skipped: Array.from(new Set([...(answers.skipped || []), id])) });
  const isSkipped = (id) => (answers.skipped || []).includes(id);
  const typeKey = { agreement: "agreement_status", partner_perspective: "partner_perspective_type", pattern: "pattern_type", own_actions: "own_actions_status" };
  return (
    <section data-testid="ask-context-check" className="ss-card-premium p-5 sm:p-6 border border-gold/40">
      <p className="text-[10px] tracking-[0.3em] uppercase text-mauve inline-flex items-center gap-1.5"><HelpCircle size={12} /> Help me understand the situation</p>
      <p className="mt-2 text-cocoa/75 text-sm leading-relaxed" data-testid="ask-context-check-intro">{check.intro}</p>
      {check.safety?.show_safety_route && (
        <p className="mt-3 text-[13px] text-cocoa/80 bg-blush/40 border border-rose/40 rounded-md p-3 inline-flex items-start gap-2" data-testid="ask-safety-route">
          <ShieldAlert size={14} className="shrink-0 mt-0.5 text-rose" />
          <span>If anything about this feels unsafe, you can skip straight to <Link to="/safety" className="underline">confidential support options</Link> — no questions first.</span>
        </p>
      )}
      <div className="mt-4 space-y-4">
        {check.questions.map((q) => (
          <div key={q.id} data-testid={`ask-cc-q-${q.id}`} className={isSkipped(q.id) ? "opacity-50" : ""}>
            <p className="text-cocoa text-[15px]">{q.text}</p>
            {q.hint && <p className="text-xs text-cocoa/55 mt-0.5">{q.hint}</p>}
            {q.kind === "choice" ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {q.options.map((o) => (
                  <button type="button" key={o} onClick={() => set(q.id, o)} data-testid={`ask-cc-${q.id}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`ss-pill h-9 px-3 text-xs ${answers[q.id] === o ? "ss-pill-primary" : "ss-pill-ghost"}`}>{o}</button>
                ))}
              </div>
            ) : (
              <>
                <textarea rows={2} maxLength={800} value={answers[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} data-testid={`ask-cc-${q.id}-text`}
                  disabled={isSkipped(q.id)} placeholder="In your own words — as much or as little as you like" className={`${field} mt-2`} />
                {q.options && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {q.options.map((o) => (
                      <button type="button" key={o} onClick={() => onChange({ ...answers, [typeKey[q.id] || `${q.id}_status`]: o })} data-testid={`ask-cc-${q.id}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`ss-pill h-8 px-3 text-xs ${answers[typeKey[q.id] || `${q.id}_status`] === o ? "ss-pill-primary" : "ss-pill-ghost"}`}>{o}</button>
                    ))}
                  </div>
                )}
              </>
            )}
            {!isSkipped(q.id) && <button type="button" onClick={() => skip(q.id)} data-testid={`ask-cc-${q.id}-skip`} className="mt-1.5 text-xs text-cocoa/50 hover:text-cocoa underline">Skip this one</button>}
          </div>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap gap-2 items-center">
        <button type="button" onClick={onAnswered} disabled={busy} data-testid="ask-cc-continue" className="ss-pill ss-pill-primary h-11 px-6 disabled:opacity-50">{busy ? "Reading…" : <><ArrowRight size={14} /> Continue</>}</button>
        <button type="button" onClick={onSkipAll} disabled={busy} data-testid="ask-cc-skip-all" className="ss-pill ss-pill-ghost h-11 px-5 disabled:opacity-50">Continue with what I've shared</button>
      </div>
      <p className="mt-3 text-[11px] text-cocoa/50">Skipping never counts against you. Anything you leave blank is treated as unknown — not as "nothing happened".</p>
    </section>
  );
}

const List = ({ items, testId }) => Array.isArray(items) && items.length > 0 ? (
  <ul className="mt-2 space-y-1.5 text-sm text-cocoa/85 leading-relaxed list-disc pl-5" data-testid={testId}>{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
) : null;

/* A–E elements rendered only where present. */
export function BalancedView({ b }) {
  if (!b || typeof b !== "object") return null;
  const any = b.account_supports || b.unclear?.length || b.your_next_steps?.length || b.their_responsibility?.length || b.together_next_step || b.uncertainty_note;
  if (!any) return null;
  return (
    <div className="ss-card-premium p-5 sm:p-6" data-testid="ask-balanced">
      <p className="text-[10px] tracking-[0.3em] uppercase text-mauve inline-flex items-center gap-1.5"><Scale size={12} /> Both sides, as far as your account allows</p>
      {b.account_supports && (<><p className="mt-3 text-[10px] uppercase tracking-widest text-cocoa/50">What your account supports</p><p className="mt-1 text-sm text-cocoa/85 leading-relaxed" data-testid="ask-balanced-supports">{b.account_supports}</p></>)}
      {b.unclear?.length > 0 && (<><p className="mt-4 text-[10px] uppercase tracking-widest text-cocoa/50">What's still unclear</p><List items={b.unclear} testId="ask-balanced-unclear" /></>)}
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        {b.your_next_steps?.length > 0 && (<div className="bg-ivory/70 rounded-md p-3 border border-champagne"><p className="text-[10px] uppercase tracking-widest text-sage">Your available next steps</p><List items={b.your_next_steps} testId="ask-balanced-yours" /></div>)}
        {b.their_responsibility?.length > 0 && (<div className="bg-ivory/70 rounded-md p-3 border border-champagne"><p className="text-[10px] uppercase tracking-widest text-mauve">What remains theirs to address</p><List items={b.their_responsibility} testId="ask-balanced-theirs" /></div>)}
      </div>
      {b.together_next_step && (<><p className="mt-4 text-[10px] uppercase tracking-widest text-cocoa/50">A possible step together</p><p className="mt-1 text-sm text-cocoa/85 leading-relaxed" data-testid="ask-balanced-together">{b.together_next_step}</p></>)}
      {b.uncertainty_note && <p className="mt-4 text-xs text-cocoa/55 italic" data-testid="ask-balanced-uncertainty">{b.uncertainty_note}</p>}
    </div>
  );
}

/* "Something's off? Correct it." — stores a correction with provenance and supersedes derived memory. */
export function CorrectAdvisor({ sessionId }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async () => {
    setBusy(true);
    try { const r = await api.post(`/advisor/sessions/${sessionId}/correct`, { text: text.trim() }); setDone(r.data); toast.success("Correction saved — we won't treat the old reading as current."); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  if (done) return <p className="mt-3 text-xs text-sage inline-flex items-center gap-1.5" data-testid="ask-correct-done"><CheckCircle2 size={13} /> Correction saved. {done.superseded?.events || 0} memory event(s) and {done.superseded?.vectors || 0} recall item(s) from this read were set aside.</p>;
  return (
    <div className="mt-3" data-testid="ask-correct">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="ask-correct-open" className="text-xs text-cocoa/55 hover:text-cocoa inline-flex items-center gap-1"><PenLine size={12} /> Something's off? Correct what we understood</button>
      ) : (
        <div className="ss-card p-4 space-y-2">
          <p className="text-sm text-cocoa/80">Tell us what we got wrong or what you'd now put differently. We keep the history but stop treating the old reading as current.</p>
          <textarea rows={3} maxLength={1500} value={text} onChange={(e) => setText(e.target.value)} data-testid="ask-correct-text" className={field} placeholder="e.g. Actually he had told me the day before — I'd forgotten." />
          <div className="flex gap-2">
            <button type="button" onClick={submit} disabled={busy || text.trim().length < 3} data-testid="ask-correct-submit" className="ss-pill ss-pill-primary h-9 px-4 text-xs disabled:opacity-50">{busy ? "Saving…" : "Save correction"}</button>
            <button type="button" onClick={() => setOpen(false)} className="ss-pill ss-pill-ghost h-9 px-4 text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const COUNTRIES = [["", "Choose your country…"], ["AU", "Australia"], ["GB", "United Kingdom"], ["US", "United States"], ["NZ", "New Zealand"], ["CA", "Canada"], ["IE", "Ireland"], ["EU", "Other EU country"], ["XX", "Somewhere else"]];
export const getSavedCountry = () => { try { return localStorage.getItem("ss_country") || ""; } catch { return ""; } };

/* Safety-first card: verified local numbers from the product table; never model-generated. */
export function SafetySupport({ support, onCountry }) {
  const [country, setCountry] = useState(getSavedCountry());
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(false);
  if (!support) return null;
  const pick = (c) => { setCountry(c); try { localStorage.setItem("ss_country", c); } catch {} onCountry?.(c); };
  const markChanged = async () => {
    if (!support.carried_session_id) return;
    setResolving(true);
    try {
      await api.post(`/advisor/sessions/${support.carried_session_id}/correct`, { text: "Status update: what I described earlier about my safety no longer applies.", reason: "safety_status_update" });
      setResolved(true); toast.success("Thank you — we've recorded that this has changed.");
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setResolving(false); }
  };
  return (
    <section data-testid="ask-safety-support" className="ss-card p-5 sm:p-6 border-2 border-rose/60 bg-blush/30">
      <p className="text-[10px] tracking-[0.3em] uppercase text-rose inline-flex items-center gap-1.5"><ShieldAlert size={12} /> {support.carried ? "Keeping your safety in view" : "Your safety comes first"}</p>
      {support.carried && support.carried_note && (
        <div className="mt-2 text-xs text-cocoa/75 leading-relaxed" data-testid="ask-safety-carried">
          <p>{support.carried_note}</p>
          {!resolved ? (
            <button type="button" onClick={markChanged} disabled={resolving} data-testid="ask-safety-no-longer-applies" className="mt-2 ss-pill ss-pill-ghost h-8 text-xs">This no longer applies</button>
          ) : <p className="mt-2 text-mauve" data-testid="ask-safety-resolved">Recorded. Future reads about this person won't carry it forward.</p>}
        </div>
      )}
      <p className="mt-2 font-heading text-xl text-cocoa" data-testid="ask-safety-headline">{support.headline}</p>
      <p className="mt-2 text-sm text-cocoa/85 leading-relaxed" data-testid="ask-safety-message">{support.message}</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <div className="bg-ivory rounded-md p-3 border border-champagne">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/55">Emergency / police{support.country_name ? ` · ${support.country_name}` : ""}</p>
          {support.emergency ? (
            <p className="mt-1 text-2xl font-heading text-cocoa" data-testid="ask-safety-emergency">{support.emergency}{support.police_non_emergency && <span className="block text-xs text-cocoa/60 font-sans mt-0.5">Police (non-emergency): {support.police_non_emergency}</span>}</p>
          ) : <p className="mt-1 text-sm text-cocoa/75">{support.fallback}</p>}
        </div>
        <div className="bg-ivory rounded-md p-3 border border-champagne">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/55">Confidential helpline</p>
          {support.helpline ? (
            <p className="mt-1 text-sm text-cocoa" data-testid="ask-safety-helpline"><strong>{support.helpline.name}</strong> · <a href={`tel:${support.helpline.phone.replace(/[^0-9+]/g, "")}`} className="underline">{support.helpline.phone}</a> <span className="text-cocoa/55">({support.helpline.hours})</span></p>
          ) : <p className="mt-1 text-sm text-cocoa/75" data-testid="ask-safety-helpline-fallback">{support.fallback || "Search for your national domestic violence helpline."}</p>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="text-xs text-cocoa/60">Show numbers for</label>
        <select data-testid="ask-safety-country" value={country || support.country || ""} onChange={(e) => pick(e.target.value)} className="bg-ivory border border-champagne rounded-md h-9 px-2 text-sm">
          {COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span className="text-[11px] text-cocoa/50">Numbers verified {support.verified_on} against the operators' official sites.</span>
      </div>
      {support.do_not?.length > 0 && <ul className="mt-3 text-xs text-cocoa/60 list-disc pl-5 space-y-0.5">{support.do_not.map((d, i) => <li key={i}>{d}</li>)}</ul>}
      <p className="mt-3 text-xs text-cocoa/70"><Link to="/safety" className="underline">More support options</Link> · We never contact anyone on your behalf.</p>
    </section>
  );
}

/* One-tap: save an agreement or pattern she mentioned into the persona's context memory. */
export function ContextSuggestions({ suggestions, profileId }) {
  const [saved, setSaved] = useState({});
  const [busy, setBusy] = useState(null);
  if (!suggestions?.length || !profileId) return null;
  const save = async (sg, i) => {
    setBusy(i);
    try {
      const p = (await api.get(`/profiles/${profileId}`)).data;
      const cur = (p[sg.field] || "").trim();
      await api.put(`/profiles/${profileId}`, { [sg.field]: cur ? `${cur}\n${sg.text}` : sg.text });
      setSaved({ ...saved, [i]: true }); toast.success("Saved to this person's context");
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(null); }
  };
  return (
    <div className="ss-card p-4" data-testid="ask-context-suggestions">
      <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">Remember this for next time?</p>
      <ul className="mt-2 space-y-2">
        {suggestions.map((sg, i) => (
          <li key={i} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`ask-ctx-suggestion-${i}`}>
            <span className="text-cocoa/85">“{sg.text}”</span>
            {saved[i] ? <span className="text-xs text-sage inline-flex items-center gap-1"><CheckCircle2 size={12} /> Saved</span>
              : <button type="button" disabled={busy === i} onClick={() => save(sg, i)} data-testid={`ask-ctx-suggestion-${i}-save`} className="ss-pill ss-pill-ghost h-8 px-3 text-xs">{busy === i ? "Saving…" : sg.label}</button>}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-cocoa/50">Saved as your account of things — you can edit or remove it on the profile page.</p>
    </div>
  );
}
